import React from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { WidgetApiKeyModel } from '../api/magicLinkApi';
import useTexts from '../hooks/useTexts';

interface ApiKeyListProps {
  apiKeys: WidgetApiKeyModel[];
  onSelect: (apiKey: WidgetApiKeyModel) => void;
  onLogout: () => void;
  isLoading: boolean;
  error?: string;
}

const ApiKeyList: React.FC<ApiKeyListProps> = ({
  apiKeys,
  onSelect,
  isLoading,
  error,
}) => {
  const texts = useTexts();

  const formatDomains = (shopUrls?: string[]) => {
    if (!shopUrls || shopUrls.length === 0) return '';
    return shopUrls.join(', ');
  };

  return (
    <Box width="100%" maxWidth={659} mx="auto" mt={{ sm: 0, md: 4 }}>
      <Box sx={{ mb: { xs: 4, md: 6 } }}>
        <Typography variant="h1" gutterBottom>
          {texts.SELECT_CONFIG_TITLE}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {texts.SELECT_CONFIG_DESC}
        </Typography>
      </Box>

      {apiKeys.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {texts.NO_API_KEYS}
        </Typography>
      ) : (
        <Box
          sx={{
            display: { sm: 'block', md: 'flex' },
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {apiKeys.map((apiKey) => (
            <Box
              key={apiKey.apiKey}
              sx={{
                display: { sm: 'block', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #D3D3D3',
                pb: 2.5,
                pt: { xs: 2.5, md: 0 },
                '&::after': {
                  content: '""',
                  display: { xs: 'block', md: 'none' },
                  clear: 'both',
                },
              }}
            >
              <Box sx={{ mr: { sm: 0, md: 3 }, mb: { sm: 2, md: 0 }, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {texts.API_KEY}:
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {apiKey.apiKey}
                  </Box>
                  {!apiKey.isActive && (
                    <Chip
                      size="small"
                      label={texts.KEY_INACTIVE}
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 400 }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {texts.DOMAINS_LABEL}:
                  </Box>
                  {'  '}
                  {apiKey.shopUrls?.length === 0
                    ? '/'
                    : formatDomains(apiKey.shopUrls)}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => onSelect(apiKey)}
                disabled={isLoading}
                sx={{
                  mt: { xs: 1, md: 0 },
                  float: { xs: 'right', md: 'none' },
                  flexShrink: 0,
                  width: 160,
                  height: 49,
                  borderColor: (t: any) => t.custom.outlinedBorder,
                  color: 'primary.main',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: '25px',
                  letterSpacing: 0,
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#004B93',
                    color: '#fff',
                    borderColor: '#004B93',
                  },
                }}
              >
                {texts.EDIT_BUTTON}
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {error && (
        <Alert severity="error" icon={<WarningAmber />} sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ApiKeyList;
